/**
 * AvenBoxMini — condensed scroll-locked product reveal for the homepage.
 *
 * ~2.5 viewports of pin (vs 7 on /avenbox). Front → side → ports tilt → xray.
 * No annotation panels — pure cinematic device moment. Ends with CTA into /avenbox.
 */

import { lazy, Suspense, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Canvas } from '@react-three/fiber';
import { motion, useMotionValue, useTransform, animate, type MotionValue } from 'framer-motion';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import type { AvenBoxScrollState } from './AvenBoxLite';

gsap.registerPlugin(ScrollTrigger, useGSAP);

const AvenBoxLite = lazy(() => import('./AvenBoxLite'));

// Compressed sequence. Each hold ~12–15% of pin.
function computeMiniState(p: number): AvenBoxScrollState {
    let rotY: number;
    if      (p < 0.12) rotY = 0;
    else if (p < 0.25) rotY = gsap.utils.mapRange(0.12, 0.25, 0,    0.75, p);
    else if (p < 0.42) rotY = 0.75;
    else               rotY = 0.75;

    const HALF_PI = Math.PI / 2;
    let rotX: number;
    if      (p < 0.42) rotX = 0.04;
    else if (p < 0.55) rotX = gsap.utils.mapRange(0.42, 0.55, 0.04, -HALF_PI, p);
    else if (p < 0.75) rotX = -HALF_PI;
    else if (p < 0.82) rotX = gsap.utils.mapRange(0.75, 0.82, -HALF_PI, 0.04, p);
    else               rotX = 0.04;

    const vent =
        p < 0.25 ? 0 :
        p < 0.32 ? gsap.utils.clamp(0, 1, (p - 0.25) / 0.07) :
        p < 0.42 ? 1 :
        p < 0.49 ? gsap.utils.clamp(0, 1, 1 - (p - 0.42) / 0.07) : 0;

    const xray = p >= 0.82 ? gsap.utils.clamp(0, 1, (p - 0.82) / 0.10) : 0;

    return { rotY, rotX, xray, vent };
}

function Caption({ smoothProgress }: { smoothProgress: MotionValue<number> }) {
    const opacity = useTransform(smoothProgress, [0, 0.04, 0.92, 1], [0, 1, 1, 0]);
    return (
        <motion.div className="avenbox-mini__caption" style={{ opacity }}>
            <span className="avenbox-mini__eyebrow">The Product</span>
            <span className="avenbox-mini__title">
                AvenBox. <em>In motion.</em>
            </span>
        </motion.div>
    );
}

function EndCTA({ smoothProgress }: { smoothProgress: MotionValue<number> }) {
    const opacity = useTransform(smoothProgress, [0.78, 0.92], [0, 1]);
    const y = useTransform(smoothProgress, [0.78, 0.92], [24, 0]);
    return (
        <motion.div className="avenbox-mini__cta" style={{ opacity, y }}>
            <Link to="/avenbox" className="avenbox-mini__cta-link">
                See full breakdown
                <span aria-hidden>→</span>
            </Link>
        </motion.div>
    );
}

export default function AvenBoxMini() {
    const stickyRef   = useRef<HTMLDivElement>(null);
    const scrollState = useRef<AvenBoxScrollState>({ rotY: 0, rotX: 0.04, xray: 0, vent: 0 });
    const smoothProgress = useMotionValue<number>(0);

    useGSAP(() => {
        const el = stickyRef.current;
        if (!el) return;

        ScrollTrigger.create({
            trigger:    el,
            pin:        true,
            pinSpacing: true,
            start:      'top top',
            end:        '+=250%',
            onUpdate: (self) => {
                const p = self.progress;
                scrollState.current = computeMiniState(p);
                animate(smoothProgress, p, { duration: 0, ease: 'linear' });
            },
        });
    }, { scope: stickyRef });

    return (
        <div className="avenbox-mini">
            <div ref={stickyRef} className="avenbox-mini__sticky">
                <Canvas
                    camera={{ position: [0, 0, 16], fov: 42 }}
                    dpr={[1, 1.5]}
                    gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
                    style={{ position: 'absolute', inset: 0 }}
                >
                    <ambientLight intensity={0.12} />
                    <directionalLight position={[5, 9, 7]}   intensity={7.5} color="#ffffff" />
                    <directionalLight position={[-6, 2, 4]}  intensity={2.8} color="#d8e8ff" />
                    <directionalLight position={[0, 6, -10]} intensity={4.5} color="#ffffff" />
                    <directionalLight position={[0, -7, 3]}  intensity={0.6} color="#d0d8e8" />

                    <Suspense fallback={null}>
                        <group position={[0, -0.5, 0]}>
                            <AvenBoxLite scrollState={scrollState} />
                        </group>
                    </Suspense>
                </Canvas>

                <Caption smoothProgress={smoothProgress} />
                <EndCTA  smoothProgress={smoothProgress} />
            </div>
        </div>
    );
}
